import { FormFieldType, ICustomLeadParser, IFormField, IOperationResult, IParsedLead } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { IEventLoggingComponentProps, withEventLogging } from '../../../models/Logging';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../models/UiUtils';
import { useContactCustomFields } from '../../../queries/Contact/useContactCustomFields';
import { AccountOperationsViewModel } from '../../../viewmodels/AdminViewModels';
import { RichContentDocumentEditor } from '../../components/richContent/RichContentDocumentEditor';
import { baseStyleSheet } from '../../styles/styles';
import { LoadingSpinner } from '../LoadingSpinner';
import { asModalComponent } from '../Modal';
import { styleSheet } from './styles';

const translateCustomFieldValue = (rawValue: string | null | undefined, field?: IFormField<string>): string => {
	if (rawValue == null || rawValue === '') {
		return '';
	}
	if (field?.fieldType === FormFieldType.Option || field?.fieldType === FormFieldType.MultipleOptions) {
		const ids = rawValue
			.split(',')
			.map(s => s.trim())
			.filter(Boolean);
		return ids.map(id => field.options?.find(o => o.id === id)?.label ?? id).join(', ');
	}
	return rawValue;
};

interface IProps extends IEventLoggingComponentProps {
	parser: ICustomLeadParser;
	onClose(): void;
	tagVM?: AccountOperationsViewModel;
}

const idPrefix = 'test-lead-parser';

const BaseTestLeadParserModal: React.FC<IProps> = observer(({ onClose, parser, tagVM }) => {
	const [testFile, setTestFile] = useState<File | null>(null);
	const [testResults, setTestResults] = useState<IOperationResult<IParsedLead[]> | null>(null);
	const [testingParser, setTestingParser] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const customFieldsQuery = useContactCustomFields({
		enabled: !!tagVM?.account?.id,
		impersonationContext: tagVM?.account ? { account: tagVM.account } : undefined,
		refetchOnWindowFocus: false,
	});
	const customFieldsByName = useMemo(() => {
		const map = new Map<string, IFormField<string>>();
		customFieldsQuery.data?.fields?.forEach(f => {
			if (f.name) {
				map.set(f.name, f);
			}
		});
		return map;
	}, [customFieldsQuery.data]);

	const renderFormattedLeadResults = (results: IParsedLead[]) => (
		<div className={css(styleSheet.formattedResults)}>
			{results.map((result, index) => (
				<div key={index} className={css(styleSheet.leadResultCard)}>
					{result.contact?.tags && result.contact.tags.length > 0 && (
						<div className={css(styleSheet.resultSection)}>
							<h4 className={css(styleSheet.resultSectionTitle)}>Tags</h4>
							<div className={css(styleSheet.tagBadge)}>
								{result.contact.tags.map((tag, tagIndex) => (
									<span key={tagIndex} className={css(styleSheet.tag)}>
										{tag}
									</span>
								))}
							</div>
						</div>
					)}
					{result.contact?.keyFactsCollection && result.contact.keyFactsCollection.length > 0 && (
						<div className={css(styleSheet.resultSection)}>
							<h4 className={css(styleSheet.resultSectionTitle)}>Key Facts</h4>
							<div className={css(styleSheet.tagBadge)}>
								{result.contact.keyFactsCollection.map((keyFact, kfIndex) => (
									<span key={kfIndex} className={css(styleSheet.tag)}>
										{keyFact.value}
									</span>
								))}
							</div>
						</div>
					)}
					{result.contact && (
						<div className={css(styleSheet.resultSection)}>
							<h4 className={css(styleSheet.resultSectionTitle)}>Contact</h4>
							<div className={css(styleSheet.contactDetails)}>
								{(result.contact.firstName || result.contact.lastName) && (
									<div className={css(styleSheet.contactField)}>
										<span className={css(styleSheet.fieldLabel)}>Name:</span>
										<span className={css(styleSheet.fieldValue)}>
											{[result.contact.firstName, result.contact.lastName].filter(Boolean).join(' ')}
										</span>
									</div>
								)}
								{result.contact.firstName && (
									<div className={css(styleSheet.contactField)}>
										<span className={css(styleSheet.fieldLabel)}>First Name:</span>
										<span className={css(styleSheet.fieldValue)}>{result.contact.firstName}</span>
									</div>
								)}
								{result.contact.lastName && (
									<div className={css(styleSheet.contactField)}>
										<span className={css(styleSheet.fieldLabel)}>Last Name:</span>
										<span className={css(styleSheet.fieldValue)}>{result.contact.lastName}</span>
									</div>
								)}
								{result.contact.companyName && (
									<div className={css(styleSheet.contactField)}>
										<span className={css(styleSheet.fieldLabel)}>Company Name:</span>
										<span className={css(styleSheet.fieldValue)}>{result.contact.companyName}</span>
									</div>
								)}
								{result.contact.jobTitle && (
									<div className={css(styleSheet.contactField)}>
										<span className={css(styleSheet.fieldLabel)}>Job Title:</span>
										<span className={css(styleSheet.fieldValue)}>{result.contact.jobTitle}</span>
									</div>
								)}
								{result.contact.emailAddresses && result.contact.emailAddresses.length > 0 && (
									<div className={css(styleSheet.contactField)}>
										<span className={css(styleSheet.fieldLabel)}>Email:</span>
										<span className={css(styleSheet.fieldValue)}>
											{result.contact.emailAddresses.map(email => email.value).join(', ')}
										</span>
									</div>
								)}
								{result.contact.phoneNumbers && result.contact.phoneNumbers.length > 0 && (
									<div className={css(styleSheet.contactField)}>
										<span className={css(styleSheet.fieldLabel)}>Phone:</span>
										<span className={css(styleSheet.fieldValue)}>
											{result.contact.phoneNumbers.map(phone => phone.value).join(', ')}
										</span>
									</div>
								)}
								{(() => {
									const address = result.contact?.address;
									if (!address) return null;
									const cityStateLine = [address.city, address.stateProvince].filter(s => s?.trim()).join(', ');
									const localityLine = [cityStateLine, address.postalCode].filter(s => s?.trim()).join(' ');
									const lines = [address.address1, address.address2, localityLine, address.country].filter(s =>
										s?.trim()
									) as string[];
									if (lines.length === 0) return null;
									return (
										<div className={css(styleSheet.contactField)}>
											<span className={css(styleSheet.fieldLabel)}>Address:</span>
											<span className={css(styleSheet.fieldValue, styleSheet.addressLines)}>
												{lines.map((line, i) => (
													<span key={i}>{line}</span>
												))}
											</span>
										</div>
									);
								})()}
							</div>
						</div>
					)}
					{result.customFields &&
						Object.keys(result.customFields).length > 0 &&
						(() => {
							const entries = Object.entries(result.customFields).filter(([, value]) => value != null && value !== '');
							if (entries.length === 0) return null;
							return (
								<div className={css(styleSheet.resultSection)}>
									<h4 className={css(styleSheet.resultSectionTitle)}>Custom Fields</h4>
									<div className={css(styleSheet.contactDetails)}>
										{entries.map(([name, value]) => {
											const field = customFieldsByName.get(name);
											const label = field?.label ?? name;
											const displayValue = translateCustomFieldValue(value, field);
											return (
												<div key={name} className={css(styleSheet.contactField)}>
													<span className={css(styleSheet.fieldLabel)}>{label}:</span>
													<span className={css(styleSheet.fieldValue)}>{displayValue}</span>
												</div>
											);
										})}
									</div>
								</div>
							);
						})()}
					{result.note && (
						<div className={css(styleSheet.resultSection)}>
							<h4 className={css(styleSheet.resultSectionTitle)}>Note</h4>
							<div className={css(styleSheet.noteContent)}>
								<RichContentDocumentEditor
									contentState={convertRawRichTextContentStateToRichContentEditorState(result.note)}
									readOnly={true}
								/>
							</div>
						</div>
					)}
				</div>
			))}
			<p className={css(styleSheet.simulationCaveat)}>
				Test mode shows what the parser extracted from the email. On a real lead, custom-field values only get applied
				to fields that are currently empty on the matched contact (existing values are never overwritten).
			</p>
		</div>
	);

	const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			setTestFile(e.target.files[0]);
			setTestResults(null);
		}
	};
	const onTestParser = async () => {
		if (!testFile || !parser?.name) return;

		setTestingParser(true);
		setTestResults(null);
		try {
			const formData = new FormData();
			formData.append('files', testFile);

			const accountId = tagVM?.account?.id;
			if (!accountId) {
				throw new Error('Account or user information is missing');
			}

			// Note: If we need ever test parsing as a specific user, we need to explicitly add that to the modal for selection.
			await new Promise(resolve => {
				const parserId = parser?.id;
				const customLeadParserIdsParam = parserId ? `&customLeadParserIds=${parserId}` : '';

				tagVM?.userSession?.webServiceHelper?.callWebServiceWithBulkOperationResult(
					`impersonate/${accountId}/admin/emailOperations/ParseEmailMessage?${customLeadParserIdsParam}`,
					'POST',
					formData,
					(result: IOperationResult<IParsedLead[]>) => {
						setTestResults(result);
						resolve(result);
					},
					(error: any) => {
						const operationResult = error as IOperationResult<any>;

						setTestResults(operationResult);

						resolve(operationResult);
					}
				);
			});
		} catch (error) {
			console.error('Error in test parser function:', error);

			setTestResults({
				success: false,
				systemMessage: error instanceof Error ? error.message : 'An error occurred while testing the parser',
			});
		} finally {
			setTestingParser(false);
		}
	};

	return (
		<div className={css(styleSheet.container)}>
			<h1 className={css(styleSheet.title)}>Test Lead Parser</h1>
			<div className={css(styleSheet.body)}>
				<p className={css(styleSheet.text, styleSheet.bodyText)}>
					Upload an EML file to test the parser configuration{parser?.name ? ` for ${parser.name}` : ''}.
				</p>
				<div className={css(styleSheet.fileInputContainer)}>
					<input
						type='file'
						ref={fileInputRef}
						accept='.eml'
						onChange={onFileChange}
						className={css(styleSheet.fileInput)}
					/>
				</div>
				{testResults && (
					<div className={css(styleSheet.testResults)}>
						<h3 className={css(styleSheet.testResultsTitle)}>Test Results:</h3>{' '}
						{!testResults.success ? (
							<div className={css(styleSheet.testError)}>
								{testResults.systemCode === 404 ? (
									<>
										The parser cannot handle this EML file format.
										<br />
										Please check the config and try again.
									</>
								) : (
									'Error: ' + testResults.systemMessage
								)}
							</div>
						) : Array.isArray(testResults.value) && testResults.value.length > 0 ? (
							<div>
								<p>Successfully parsed lead:</p>
								{renderFormattedLeadResults(testResults.value)}
							</div>
						) : (
							<div className={css(styleSheet.testWarning)}>
								No leads were found. Check your parser configuration and try again.
							</div>
						)}
					</div>
				)}
			</div>
			<div className={css(styleSheet.buttonsContainer)}>
				{testingParser ? (
					<LoadingSpinner type='small' />
				) : (
					<div className={css(styleSheet.buttonGroup)}>
						<button
							className={css(baseStyleSheet.ctaButton)}
							onClick={onTestParser}
							disabled={!testFile}
							aria-label='Run Test Lead Parser'
						>
							Run Test
						</button>
						<button
							className={css(baseStyleSheet.ctaButtonReverse)}
							onClick={onClose}
							aria-label='Close Test Lead Parser Modal'
						>
							Close
						</button>
					</div>
				)}
			</div>
		</div>
	);
});

const TestLeadParserModalWithLogging = withEventLogging(BaseTestLeadParserModal, 'TestLeadParser');

export const TestLeadParserModal = asModalComponent(TestLeadParserModalWithLogging, {
	className: `${idPrefix} ${css(styleSheet.modal)}`,
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
